import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpRight,
  Download,
  Apple,
  Smartphone,
  Monitor,
  ShieldCheck,
  Check,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
// Версии и ссылки на сборки, в src/data/downloads.ts (общие с JSON-LD в <head>).
import {
  APK_URL,
  APK_VERSION,
  APK_SIZE,
  ANDROID_MIN,
  IOS_URL,
  IOS_MIN,
  WIN_URL,
  WIN_VERSION,
  WIN_SIZE,
  WIN_MIN,
  APP_ICON,
  WAITER_APK_URL,
  WAITER_APK_VERSION,
  WAITER_APK_SIZE,
  WAITER_ICON,
} from '@/data/downloads';

const INSTALL_STEPS = [
  'Скачайте APK кнопкой выше, файл сохранится в «Загрузки».',
  'Откройте файл. Android спросит разрешение установить приложение из этого источника, разрешите.',
  'Подтвердите установку. Иконка Resstro появится на рабочем столе.',
  'Запустите приложение и введите 6-значный код привязки из админки: Устройства → Сгенерировать.',
];

const WIN_STEPS = [
  'Скачайте архив кнопкой выше и распакуйте: ПКМ по файлу → «Извлечь всё».',
  'Запустите Setup.exe. Права администратора не нужны: касса ставится в профиль пользователя.',
  'Окно «Система Windows защитила ваш компьютер» показывает SmartScreen: у нового файла нет репутации у Microsoft. Нажмите «Подробнее» → «Выполнить в любом случае». Спрашивает один раз на компьютер.',
  'Касса откроется во весь экран и будет запускаться при входе в Windows. Введите 6-значный код привязки из админки: Устройства → Сгенерировать.',
];

const WAITER_STEPS = [
  'Скачайте APK на телефон официанта и откройте файл. Android спросит разрешение установить приложение из этого источника, разрешите.',
  'В админке откройте Устройства → Официанты → «Подключить официанта»: выберите официанта или впишите имя нового.',
  'Официант вводит в приложении 6-значный код — телефон подключён. Код действует 5 минут.',
  'Заказы уходят на кассу, пока на ней открыта смена: столы заводит кассир, касса печатает кухонные листы и принимает оплату.',
];

function AndroidGlyph({ className }: { className?: string }) {
  // Лёгкий robot-glyph Android (lucide не содержит бренд-логотип).
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M6 18a1 1 0 0 0 1 1h1v3.25a1.75 1.75 0 0 0 3.5 0V19h1v3.25a1.75 1.75 0 0 0 3.5 0V19h1a1 1 0 0 0 1-1V9H6v9ZM3.75 9A1.75 1.75 0 0 0 2 10.75v5.5a1.75 1.75 0 0 0 3.5 0v-5.5A1.75 1.75 0 0 0 3.75 9Zm16.5 0a1.75 1.75 0 0 0-1.75 1.75v5.5a1.75 1.75 0 0 0 3.5 0v-5.5A1.75 1.75 0 0 0 20.25 9ZM15.53 2.16l1.3-1.3a.4.4 0 0 0-.57-.56l-1.48 1.48A6.06 6.06 0 0 0 12 1.25c-.99 0-1.93.2-2.78.53L7.74.3a.4.4 0 1 0-.57.56l1.3 1.3A6.02 6.02 0 0 0 6 7.5h12a6.02 6.02 0 0 0-2.47-5.34ZM9.75 5.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm4.5 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
    </svg>
  );
}

function WindowsGlyph({ className }: { className?: string }) {
  // Четыре плитки Windows. Геометрия, из src/images/icons8-windows-10.svg, но
  // цвет отдан currentColor: марки платформ на этой странице монохромные и
  // тонируются оранжевым, и логотип в собственном голубом выбивался бы из ряда.
  return (
    <svg viewBox="0 0 48 48" fill="currentColor" className={className} aria-hidden>
      <path d="M20 25.026L5.011 25 5.012 37.744 20 39.818zM22 25.03L22 40.095 42.995 43 43 25.066zM20 8.256L5 10.38 5.014 23 20 23zM22 7.973L22 23 42.995 23 42.995 5z" />
    </svg>
  );
}

export default function Downloads() {
  return (
    <div className="min-h-screen bg-paper">
      <Header />

      <main className="section">
        <div className="container-site">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/60 transition hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            На главную
          </Link>

          <div className="mt-10 max-w-3xl">
            <span className="eyebrow">Загрузки</span>
            <h1 className="h2 mt-4">Приложение Resstro&nbsp;Касса</h1>
            <p className="mt-6 text-[15px] leading-relaxed text-ink/70">
              Рабочее место официанта и&nbsp;кассира: приём заказов за&nbsp;столами, оплаты,
              печать чеков и&nbsp;синхронизация с&nbsp;админкой в&nbsp;реальном времени.
              Установите на&nbsp;планшет, POS-терминал или Windows-моноблок
              и&nbsp;привяжите кассу 6-значным кодом из&nbsp;админки.
            </p>
          </div>

          {/* Платформы. Три карточки в ряд только на xl: на lg третья ушла бы
              в 300 px, и в таблице характеристик «Windows 10+» переносился бы
              на две строки. */}
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {/* Android */}
            <div className="flex flex-col border border-line bg-white p-7 sm:p-8">
              <div className="flex items-start gap-4">
                <img
                  src={APP_ICON}
                  alt="Resstro"
                  width={64}
                  height={64}
                  className="h-16 w-16 shrink-0 rounded-[14px] border border-line"
                />
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    <AndroidGlyph className="h-3.5 w-3.5 text-orange" />
                    Android
                  </div>
                  <h2 className="mt-1 text-[20px] font-medium leading-tight tracking-tight text-ink">
                    Resstro Касса
                  </h2>
                  <p className="mt-0.5 text-[13px] text-ink/55">
                    Для планшетов и&nbsp;POS-терминалов на&nbsp;Android
                  </p>
                </div>
              </div>

              <dl className="mt-6 grid grid-cols-3 gap-px overflow-hidden border border-line bg-line text-center">
                {[
                  ['Версия', APK_VERSION],
                  ['Размер', APK_SIZE],
                  ['Требует', ANDROID_MIN],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white px-2 py-3">
                    <dt className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-ink/45">
                      {k}
                    </dt>
                    <dd className="mt-1 text-[13px] font-semibold tabular-nums text-ink">{v}</dd>
                  </div>
                ))}
              </dl>

              <a
                href={APK_URL}
                download
                className="btn-primary mt-6 w-full py-3.5 text-[15px]"
              >
                <Download size={16} strokeWidth={2} />
                Скачать APK · {APK_VERSION}
              </a>
              <p className="mt-3 text-center text-[12px] text-ink/45">
                Прямая установка (.apk), Google Play не&nbsp;требуется.
              </p>
            </div>

            {/* iOS */}
            <div className="flex flex-col border border-line bg-white p-7 sm:p-8">
              <div className="flex items-start gap-4">
                <img
                  src={APP_ICON}
                  alt="Resstro"
                  width={64}
                  height={64}
                  className="h-16 w-16 shrink-0 rounded-[14px] border border-line"
                />
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    <Apple className="h-3.5 w-3.5 text-orange" />
                    iPhone / iPad
                  </div>
                  <h2 className="mt-1 text-[20px] font-medium leading-tight tracking-tight text-ink">
                    Resstro Касса
                  </h2>
                  <p className="mt-0.5 text-[13px] text-ink/55">
                    Официальное приложение в&nbsp;App&nbsp;Store
                  </p>
                </div>
              </div>

              <dl className="mt-6 grid grid-cols-3 gap-px overflow-hidden border border-line bg-line text-center">
                {[
                  ['Цена', 'Бесплатно'],
                  ['Магазин', 'App Store'],
                  ['Требует', IOS_MIN],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white px-2 py-3">
                    <dt className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-ink/45">
                      {k}
                    </dt>
                    <dd className="mt-1 text-[13px] font-semibold text-ink">{v}</dd>
                  </div>
                ))}
              </dl>

              <a
                href={IOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-dark mt-6 w-full py-3.5 text-[15px]"
              >
                <Apple size={16} strokeWidth={2} className="fill-current" />
                Открыть в App Store
                <ArrowUpRight size={15} strokeWidth={2} />
              </a>
              <p className="mt-3 text-center text-[12px] text-ink/45">
                Обновления приходят автоматически через&nbsp;App&nbsp;Store.
              </p>
            </div>

            {/* Windows */}
            <div className="flex flex-col border border-line bg-white p-7 sm:p-8">
              <div className="flex items-start gap-4">
                <img
                  src={APP_ICON}
                  alt="Resstro"
                  width={64}
                  height={64}
                  className="h-16 w-16 shrink-0 rounded-[14px] border border-line"
                />
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    <WindowsGlyph className="h-3.5 w-3.5 text-orange" />
                    Windows
                  </div>
                  <h2 className="mt-1 text-[20px] font-medium leading-tight tracking-tight text-ink">
                    Resstro Касса
                  </h2>
                  <p className="mt-0.5 text-[13px] text-ink/55">
                    Для кассовых моноблоков и&nbsp;ПК
                  </p>
                </div>
              </div>

              <dl className="mt-6 grid grid-cols-3 gap-px overflow-hidden border border-line bg-line text-center">
                {[
                  ['Версия', WIN_VERSION],
                  ['Размер', WIN_SIZE],
                  ['Требует', WIN_MIN],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white px-2 py-3">
                    <dt className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-ink/45">
                      {k}
                    </dt>
                    <dd className="mt-1 text-[13px] font-semibold tabular-nums text-ink">{v}</dd>
                  </div>
                ))}
              </dl>

              {/* Оранжевая, как у Android: акцентом на странице помечено то,
                  что мы раздаём сами, тёмной, переход в чужой магазин. */}
              <a href={WIN_URL} download className="btn-primary mt-6 w-full py-3.5 text-[15px]">
                <Download size={16} strokeWidth={2} />
                Скачать · {WIN_VERSION}
              </a>
              <p className="mt-3 text-center text-[12px] text-ink/45">
                Архив с&nbsp;установщиком. Дальше касса обновляется сама.
              </p>
            </div>
          </div>

          {/* Установка APK и Windows */}
          <div className="mt-14 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="flex flex-col gap-5">
              <div className="border border-line bg-white p-7 sm:p-8">
                <div className="flex items-center gap-2.5">
                  <Smartphone size={18} strokeWidth={1.75} className="text-orange" />
                  <h2 className="h3">Установка на&nbsp;Android</h2>
                </div>
                <ol className="mt-6 space-y-4">
                  {INSTALL_STEPS.map((step, i) => (
                    <li key={i} className="flex gap-3.5">
                      <span className="grid h-6 w-6 shrink-0 place-items-center bg-orange/10 text-[12px] font-semibold tabular-nums text-orange">
                        {i + 1}
                      </span>
                      <span className="text-[14px] leading-relaxed text-ink/75">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* SmartScreen, главная причина, по которой установка на Windows
                  застревает: файл без «репутации» у Microsoft, и владелец
                  решает, что установщик сломан. Поэтому шаг про «Подробнее →
                  Выполнить в любом случае» стоит на странице, а не только в
                  README внутри архива. */}
              <div className="border border-line bg-white p-7 sm:p-8">
                <div className="flex items-center gap-2.5">
                  <Monitor size={18} strokeWidth={1.75} className="text-orange" />
                  <h2 className="h3">Установка на&nbsp;Windows</h2>
                </div>
                <ol className="mt-6 space-y-4">
                  {WIN_STEPS.map((step, i) => (
                    <li key={i} className="flex gap-3.5">
                      <span className="grid h-6 w-6 shrink-0 place-items-center bg-orange/10 text-[12px] font-semibold tabular-nums text-orange">
                        {i + 1}
                      </span>
                      <span className="text-[14px] leading-relaxed text-ink/75">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="border border-line bg-white p-7 sm:p-8">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck size={18} strokeWidth={1.75} className="text-orange" />
                  <h2 className="h3">Безопасно</h2>
                </div>
                <p className="mt-4 text-[14px] leading-relaxed text-ink/70">
                  APK и&nbsp;установщик для Windows подписаны разработчиком и&nbsp;собраны
                  из&nbsp;того&nbsp;же кода, что и&nbsp;версия в&nbsp;App&nbsp;Store. Касса
                  работает только после привязки к&nbsp;вашему заведению
                  по&nbsp;одноразовому коду.
                </p>
              </div>

              <div className="border border-line bg-white p-7 sm:p-8">
                <h3 className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                  В приложении
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {[
                    'Заказы по столам и навынос',
                    'Оплата: наличные, карта, СБП, смешанная',
                    'Печать чеков и кухонных листов: сеть, USB, COM',
                    'Работа без интернета: заказы и чеки не теряются',
                    'Смены, Z-отчёты и лента заказов',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-ink/75">
                      <Check size={15} strokeWidth={2.25} className="mt-0.5 shrink-0 text-orange" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Официант — отдельное приложение: свой блок, а не четвёртая карточка
              в ряду касс. Сетка та же, что у касс, поэтому карточка встаёт
              ровно под Android-кассой. */}
          <div className="mt-16 border-t border-line pt-14">
            <div className="max-w-3xl">
              <span className="eyebrow">Для официантов</span>
              <h2 className="h2 mt-4">Приложение Resstro&nbsp;Официант</h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/70">
                Официант принимает заказ у&nbsp;стола со&nbsp;своего телефона: выбирает стол,
                добавляет блюда с&nbsp;комментарием для кухни и&nbsp;отправляет на&nbsp;кассу.
                Касса печатает кухонные листы и&nbsp;принимает оплату. Телефон подключается
                6-значным кодом из&nbsp;админки&nbsp;— как касса.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              <div className="flex flex-col border border-line bg-white p-7 sm:p-8">
                <div className="flex items-start gap-4">
                  <img
                    src={WAITER_ICON}
                    alt="Resstro Официант"
                    width={64}
                    height={64}
                    className="h-16 w-16 shrink-0 rounded-[14px] border border-line"
                  />
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                      <AndroidGlyph className="h-3.5 w-3.5 text-orange" />
                      Android
                    </div>
                    <h3 className="mt-1 text-[20px] font-medium leading-tight tracking-tight text-ink">
                      Resstro Официант
                    </h3>
                    <p className="mt-0.5 text-[13px] text-ink/55">
                      Для телефонов официантов на&nbsp;Android
                    </p>
                  </div>
                </div>

                <dl className="mt-6 grid grid-cols-3 gap-px overflow-hidden border border-line bg-line text-center">
                  {[
                    ['Версия', WAITER_APK_VERSION],
                    ['Размер', WAITER_APK_SIZE],
                    ['Требует', ANDROID_MIN],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white px-2 py-3">
                      <dt className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-ink/45">
                        {k}
                      </dt>
                      <dd className="mt-1 text-[13px] font-semibold tabular-nums text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>

                <a
                  href={WAITER_APK_URL}
                  download
                  className="btn-primary mt-6 w-full py-3.5 text-[15px]"
                >
                  <Download size={16} strokeWidth={2} />
                  Скачать APK · {WAITER_APK_VERSION}
                </a>
                <p className="mt-3 text-center text-[12px] text-ink/45">
                  Прямая установка (.apk), Google Play не&nbsp;требуется.
                </p>
              </div>

              <div className="border border-line bg-white p-7 sm:p-8 xl:col-span-2">
                <div className="flex items-center gap-2.5">
                  <Smartphone size={18} strokeWidth={1.75} className="text-orange" />
                  <h3 className="h3">Как начать</h3>
                </div>
                <ol className="mt-6 space-y-4">
                  {WAITER_STEPS.map((step, i) => (
                    <li key={i} className="flex gap-3.5">
                      <span className="grid h-6 w-6 shrink-0 place-items-center bg-orange/10 text-[12px] font-semibold tabular-nums text-orange">
                        {i + 1}
                      </span>
                      <span className="text-[14px] leading-relaxed text-ink/75">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 border border-line bg-white p-6 sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-xl">
                <div className="eyebrow">Ещё нет аккаунта</div>
                <h2 className="h3 mt-3">Сначала заведите ресторан в&nbsp;Resstro</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink/65">
                  Зарегистрируйтесь, добавьте меню и&nbsp;столы, затем сгенерируйте код
                  привязки и&nbsp;войдите в&nbsp;кассу на&nbsp;планшете. Первые 3&nbsp;дня бесплатно.
                </p>
              </div>
              <Link to="/signup" className="btn-primary">
                Начать бесплатно
                <ArrowUpRight size={15} strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
